import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Smartphone, Share, PlusSquare, RefreshCw } from "lucide-react";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

let deferredPrompt = null;
window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; window.dispatchEvent(new Event("pwa-installable")); });

export function InstallButton({ className = "", variant = "outline", size = "sm" }) {
  const [installable, setInstallable] = useState(!!deferredPrompt);
  const [showIOS, setShowIOS] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const on = () => setInstallable(true);
    const done = () => { setInstalled(true); deferredPrompt = null; };
    window.addEventListener("pwa-installable", on);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("pwa-installable", on); window.removeEventListener("appinstalled", done); };
  }, []);

  if (installed) return null;

  const click = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      deferredPrompt = null; setInstallable(false);
    } else {
      setShowIOS(true);
    }
  };

  return (
    <>
      <Button variant={variant} size={size} onClick={click} className={`rounded-full border-olive text-olive ${className}`} data-testid="pwa-install-button">
        <Smartphone className="w-4 h-4 mr-2" /> Ajouter L’Orne Park à mon téléphone
      </Button>
      <Dialog open={showIOS} onOpenChange={setShowIOS}>
        <DialogContent className="bg-white" data-testid="pwa-install-guide">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-olive">Ajouter L’Orne Park à l’écran d’accueil</DialogTitle>
            <DialogDescription>{isIOS() ? "Sur iPhone / iPad (Safari) :" : "Sur votre navigateur :"}</DialogDescription>
          </DialogHeader>
          {isIOS() ? (
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3"><span className="w-7 h-7 rounded-full bg-rosebrand-pale text-rose-brand flex items-center justify-center font-semibold shrink-0">1</span><span>Touchez le bouton <strong>Partager</strong> <Share className="inline w-4 h-4 text-olive" /> en bas de Safari.</span></li>
              <li className="flex gap-3"><span className="w-7 h-7 rounded-full bg-rosebrand-pale text-rose-brand flex items-center justify-center font-semibold shrink-0">2</span><span>Choisissez <strong>« Sur l’écran d’accueil »</strong> <PlusSquare className="inline w-4 h-4 text-olive" />.</span></li>
              <li className="flex gap-3"><span className="w-7 h-7 rounded-full bg-rosebrand-pale text-rose-brand flex items-center justify-center font-semibold shrink-0">3</span><span>Touchez <strong>Ajouter</strong>. L’icône L’Orne Park apparaît sur votre écran d’accueil : ouvrez-la pour activer les notifications.</span></li>
            </ol>
          ) : (
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3"><span className="w-7 h-7 rounded-full bg-rosebrand-pale text-rose-brand flex items-center justify-center font-semibold shrink-0">1</span><span>Ouvrez le menu du navigateur (⋮ ou ⋯).</span></li>
              <li className="flex gap-3"><span className="w-7 h-7 rounded-full bg-rosebrand-pale text-rose-brand flex items-center justify-center font-semibold shrink-0">2</span><span>Choisissez <strong>« Installer l’application »</strong> ou <strong>« Ajouter à l’écran d’accueil »</strong>.</span></li>
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function UpdateBanner() {
  const [worker, setWorker] = useState(null);
  useEffect(() => {
    const on = (e) => setWorker(e.detail);
    window.addEventListener("pwa-update-available", on);
    return () => window.removeEventListener("pwa-update-available", on);
  }, []);
  if (!worker) return null;
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] bg-olive text-white rounded-full shadow-xl px-5 py-2.5 flex items-center gap-3 text-sm" data-testid="pwa-update-banner">
      Nouvelle version disponible
      <button onClick={() => worker.postMessage("SKIP_WAITING")} className="inline-flex items-center gap-1 bg-white text-olive rounded-full px-3 py-1 font-semibold"><RefreshCw className="w-3.5 h-3.5" /> Mettre à jour</button>
    </div>
  );
}
